import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {faChevronRight, faCircleXmark, faXmark} from "@fortawesome/pro-solid-svg-icons";
import {faSpinnerThird, faSquareXmark} from "@fortawesome/pro-duotone-svg-icons";
import {ProjectFile, ProjectFileControllerService, QualityCheck, QualityControllerService} from "../../../../api";
import {StateConfigService} from "../../../../services/state/state-config.service";
import {ActivatedRoute, Router} from "@angular/router";
import {PermissionService} from "../../../../services/permission/permission.service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {QualityStepperComponent} from "../quality-stepper/quality-stepper.component";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {FilesComponent} from "../../../files/files.component";
import {BicButtonComponent, BicPopupComponent, TooltipDirective} from "@buildinginformationcloud/bic-ui-components";
import {QualitySettingsComponent} from "../quality-settings/quality-settings.component";
import {Parameter} from "../../../../quality-api";
import {MatDialog} from "@angular/material/dialog";
import {FilePrefetchService} from "../../../../services/prefetch/file-prefetch.service";
import {addMissingAncestorFolders} from "../../../../services/files/file-tree.utils";


@Component({
  selector: 'app-quality-choose-file',
  templateUrl: './quality-choose-files.component.html',
  styleUrls: ['./quality-choose-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    QualityStepperComponent,
    FaIconComponent,
    FilesComponent,
    BicButtonComponent,
    TooltipDirective,
    BicPopupComponent,

  ]
})
export class QualityChooseFilesComponent implements OnInit {
  private static readonly LV_CLASSIFICATION_ID = 'b1254874-fb9f-41d2-ae69-b3b54aa60397';
  private static readonly RB_CLASSIFICATION_ID = '7389e736-1bdd-4342-95f3-2f703bb9909a';

  /**
   * Auswählbar sind – wie auf Live – Leistungsverzeichnisse, Raumbücher und BIM-Modelle.
   *
   * LV/RB wurden bisher ausschließlich über die HOAI-Vorklassifizierung
   * ({@link LV_CLASSIFICATION_ID} / {@link RB_CLASSIFICATION_ID}) erkannt. Seit der
   * Dokumentenklassen-Verwaltung trägt eine Datei ihre Zuordnung zusätzlich in
   * {@link ProjectFile.documentClassTitles} ("Leistungsverzeichnisse", "Raumbücher"), und nur dieser
   * Weg wird beim Upload noch für den Nutzer sichtbar gepflegt. Ohne die Titel-Prüfung blieben in der
   * All-Right-Dateiauswahl deshalb nur die BIM-Modelle übrig. Beide Wege gelten, damit Bestandsprojekte
   * mit alter Klassifizierung weiterhin funktionieren.
   */
  private static readonly LV_DOCUMENT_CLASS_TOKEN = 'LEISTUNGSVERZEICHNIS';
  private static readonly RB_DOCUMENT_CLASS_TOKEN = 'RAUMBUCH';

  permissionService = inject(PermissionService);
  stateConfigService = inject(StateConfigService);
  private fileControllerService = inject(ProjectFileControllerService);
  private projectFileControllerService = inject(ProjectFileControllerService);
  private qualityControllerService = inject(QualityControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  columns = ['Name', 'Größe(MB)', 'Zuletzt geändert']
  colTypes = ['text', 'filesize', 'date']


  check: QualityCheck | undefined;

  allProjectFiles: ProjectFile[] = [];
  files: ProjectFile[] = [];
  selectedFiles: ProjectFile[] = [];
  loading = true;
  loading_check_request = false;
  protected readonly faSpinnerThird = faSpinnerThird;

  selectedRules: any[] = [];
  showConfirmationPopup: boolean = false;
  column = ['name', 'size', 'lastModified']
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private cancelCheck$ = new Subject<void>();

  parameters: any;
  protected readonly faChevronRight = faChevronRight;
  private filePrefetchService = inject(FilePrefetchService);

  get canCheckQuality(): boolean {
    return this.stateConfigService.canCheckQuality();
  }

  /** Ein All-Right-Lauf entspricht immer genau einer Prüfung des Modul-Kontingents. */
  get checkCost(): number {
    return 1;
  }

  /** Verbleibende All-Right-Prüfungen nach diesem Lauf. */
  get checksAfterRun(): number {
    const affordable = this.stateConfigService.getProjectContingent().qualityChecks ?? 0;
    return Math.max(affordable - this.checkCost, 0);
  }

  get hasSufficientChecks(): boolean {
    return (this.stateConfigService.getProjectContingent().qualityChecks ?? 0) >= this.checkCost;
  }

  get parametersOfSelectedRules(): Parameter[] {
    const parameters = this.selectedRules.map(rule => rule.parameters ?? []).flat();

    // distinct by id
    return parameters.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);
  }

  get isStartQualityCheckDisabled(): boolean {
    return this.selectedRules?.length === 0 || !this.canCheckQuality;
  }

  ngOnInit() {
    this.loadFiles();
    const state = history.state as { ifcFiles?: any[]; lvFiles?: any[]; roomBookFiles?: any[]; selectedRules?: any[] };

    if (state.selectedRules) {
      this.selectedRules = state.selectedRules;
    }
  }

  loadFiles(): void {
    this.filePrefetchService.getFiles().subscribe(files => {
      this.allProjectFiles = files;
      const filteredIFC = this.filterIFCFiles(files);
      const filteredLV = this.filterLVFiles(files);
      const filteredRB = this.filterRBFiles(files);

      this.files = [...filteredIFC, ...filteredLV, ...filteredRB];

      // check for every file if it is in a folder and if yes, add the full folder hierarchy to the files array
      this.files.forEach(file => {
        const sourceFiles = this.allProjectFiles.length > 0 ? this.allProjectFiles : this.files;
        addMissingAncestorFolders(this.files, sourceFiles, file);
      });

      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  showParameterPopUp() {
    this.showConfirmationPopup = false;
    this.cdr.markForCheck();

    if (this.parametersOfSelectedRules.length !== 0) {
      const dialogRef = this.dialog.open(QualitySettingsComponent, {
        data: {parameters: this.parametersOfSelectedRules},
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.parameters = result;
          this.showConfirmationPopup = true
          this.cdr.markForCheck();
        }
      });
    } else {
      this.parameters = {};
      this.showConfirmationPopup = true
      this.cdr.markForCheck();
    }
  }

  private static normalizeDocumentClassTitle(title: string): string {
    return String(title ?? '')
      .trim()
      .toUpperCase()
      .replace(/Ä/g, 'A')
      .replace(/Ö/g, 'O')
      .replace(/Ü/g, 'U');
  }

  filterIFCFiles(files: ProjectFile[]): ProjectFile[] {
    return files.filter(file => file.fileType == ProjectFile.FileTypeEnum.IFC && file.fileStatus != "ERROR");
  }

  filterLVFiles(files: ProjectFile[]): ProjectFile[] {
    return files.filter(file =>
      file.fileType == ProjectFile.FileTypeEnum.PDF
      && file.fileStatus != 'ERROR'
      && (this.hasClassificationRule(file, QualityChooseFilesComponent.LV_CLASSIFICATION_ID)
        || this.hasDocumentClass(file, QualityChooseFilesComponent.LV_DOCUMENT_CLASS_TOKEN))
    );
  }

  filterRBFiles(files: ProjectFile[]): ProjectFile[] {
    return files.filter(file =>
      file.fileType == ProjectFile.FileTypeEnum.EXCEL
      && file.fileStatus != 'ERROR'
      && (this.hasClassificationRule(file, QualityChooseFilesComponent.RB_CLASSIFICATION_ID)
        || this.hasDocumentClass(file, QualityChooseFilesComponent.RB_DOCUMENT_CLASS_TOKEN))
    );
  }

  /**
   * True, wenn die Datei einer Dokumentenklasse zugeordnet ist, deren Titel mit {@code token} beginnt.
   *
   * Verglichen wird auf normalisierten Titeln (Großschreibung, Umlaute aufgelöst), damit sowohl die
   * Seed-Titel im Plural ("Leistungsverzeichnisse", "Raumbücher") als auch umbenannte oder im Singular
   * gepflegte Klassen greifen.
   */
  private hasDocumentClass(file: ProjectFile, token: string): boolean {
    return (file.documentClassTitles ?? [])
      .map(title => QualityChooseFilesComponent.normalizeDocumentClassTitle(title))
      .some(title => title.startsWith(token));
  }

  private hasClassificationRule(file: ProjectFile, classificationId: string): boolean {
    const normalizedClassificationId = classificationId.toLowerCase();
    let currentFile: ProjectFile | undefined = file;
    const sourceFiles = this.allProjectFiles.length > 0 ? this.allProjectFiles : this.files;
    const visitedIds = new Set<string>();

    while (currentFile) {
      if (currentFile.classificationRules?.some(cr => cr.id?.toLowerCase() === normalizedClassificationId)) {
        return true;
      }

      if (!currentFile.parentId) {
        return false;
      }

      if (visitedIds.has(currentFile.parentId)) {
        return false;
      }
      visitedIds.add(currentFile.parentId);

      currentFile = sourceFiles.find(sourceFile => sourceFile.id === currentFile?.parentId);
    }

    return false;
  }

  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faSquareXmark = faSquareXmark;
  protected readonly faXmark = faXmark;

  cancelQualityCheck() {
    this.cancelCheck$.next();
    this.loading_check_request = false;
    this.showConfirmationPopup = false;
    this.cdr.markForCheck();
  }

  startQualityCheck() {
    this.loading_check_request = true;

    const projectFiles = {
      "IFC": this.filterIFCFiles(this.selectedFiles),
      "LV": this.filterLVFiles(this.selectedFiles),
      "RB": this.filterRBFiles(this.selectedFiles)
    };

    this.qualityControllerService.startCheck({
      projectFiles: projectFiles,
      ruleIds: this.selectedRules.map(rule => rule.id!),
      parameters: this.parameters,
    }, this.stateConfigService.getProjectId()).pipe(
      takeUntil(this.cancelCheck$)
    ).subscribe(check => {
      this.showConfirmationPopup = false;
      this.loading_check_request = false;
      this.router.navigate(['projects', this.stateConfigService.getProjectId(), 'quality', check.id, 'loading']);
    });
  }
}
